import { AlertTriangle, Play, Search, Terminal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  developers as devApi,
  type ExecutionResult,
  type ExplorerOperation,
} from '../api/resources.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { CardShell } from '../components/ui/card.js';
import { Field, Input, Textarea, inputClass } from '../components/ui/input.js';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs.js';
import { CopyButton, Empty, ErrorState, Loading } from '../components/ui.js';
import { cn } from '../lib/cn.js';
import { useAction, useAsync } from '../lib/hooks.js';

/**
 * API Explorer (PRD §32) no formato playground do produto-mãe: catálogo à esquerda,
 * requisição no meio, código à direita. A diferença é o que executa: o contrato Talkium
 * (§82), com a sessão do console, permissões reais e o request id de volta.
 */

const LANGUAGE_LABEL: Record<string, string> = {
  curl: 'cURL',
  node: 'Node.js',
  php: 'PHP',
  python: 'Python',
  csharp: 'C#',
  java: 'Java',
};

const METHOD_TONE: Record<string, string> = {
  GET: 'text-success',
  POST: 'text-primary',
  PATCH: 'text-warning',
  DELETE: 'text-danger',
};

export function ExplorerPage() {
  const operations = useAsync(() => devApi.operations(), []);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [language, setLanguage] = useState('curl');
  const [pathValues, setPathValues] = useState<Record<string, string>>({});
  const [queryValues, setQueryValues] = useState<Record<string, string>>({});
  const [bodyText, setBodyText] = useState('');
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [result, setResult] = useState<ExecutionResult | null>(null);

  const list = operations.data?.data ?? [];
  const selected = list.find((operation) => operation.operationId === selectedId) ?? null;

  // Trocar de operação zera o formulário e pré-preenche o corpo com os campos obrigatórios.
  useEffect(() => {
    if (!selected) return;
    setPathValues({});
    setQueryValues({});
    setResult(null);
    setBodyError(null);
    const required = selected.body.filter((field) => field.required);
    setBodyText(
      required.length > 0
        ? JSON.stringify(
            Object.fromEntries(
              required.map((field) => [field.name, field.example ?? defaultFor(field.type)]),
            ),
            null,
            2,
          )
        : '',
    );
  }, [selected?.operationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const parsedBody = useMemo(() => {
    if (!bodyText.trim()) return { value: undefined, error: null };
    try {
      return { value: JSON.parse(bodyText) as Record<string, unknown>, error: null };
    } catch {
      return { value: undefined, error: 'O corpo precisa ser um JSON válido.' };
    }
  }, [bodyText]);

  const samples = useAsync(
    () =>
      selected
        ? devApi.samples(selected.operationId, {
            path: pathValues,
            query: queryValues,
            ...(parsedBody.value ? { body: parsedBody.value } : {}),
          })
        : Promise.resolve(null),
    [
      selected?.operationId,
      JSON.stringify(pathValues),
      JSON.stringify(queryValues),
      parsedBody.value ? bodyText : '',
    ],
  );

  const execute = useAction(async () => {
    if (!selected) return;
    if (parsedBody.error) {
      setBodyError(parsedBody.error);
      return;
    }
    setBodyError(null);
    const path = selected.path.replace(/\{(\w+)\}/g, (_, name: string) =>
      encodeURIComponent(pathValues[name] ?? ''),
    );
    const outcome = await devApi.execute({
      method: selected.method,
      path,
      query: queryValues,
      ...(parsedBody.value !== undefined && selected.method !== 'GET'
        ? { body: parsedBody.value }
        : {}),
    });
    setResult(outcome);
  });

  if (operations.loading) return <Loading />;
  if (operations.error) return <ErrorState error={operations.error} onRetry={operations.reload} />;

  const grouped = groupByTag(
    list.filter((operation) => {
      const needle = search.trim().toLowerCase();
      if (!needle) return true;
      return `${operation.method} ${operation.path} ${operation.summary} ${operation.operationId}`
        .toLowerCase()
        .includes(needle);
    }),
  );
  const pathParams = selected?.parameters.filter((p) => p.in === 'path') ?? [];
  const queryParams = selected?.parameters.filter((p) => p.in === 'query') ?? [];
  const missingPath = pathParams.some((p) => !pathValues[p.name]);

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0 text-xl font-semibold">API Explorer</h2>
          <p className="m-0 text-sm text-muted-foreground">
            Monte a requisição, execute com a sua sessão e leve o código pronto na linguagem que
            usar.
          </p>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)_minmax(0,1fr)]">
        <CardShell
          as="section"
          aria-label="Operações"
          className="flex max-h-[calc(100vh-190px)] flex-col"
        >
          <div className="border-b border-border p-3">
            <div className="relative">
              <Search
                className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-subtle"
                aria-hidden="true"
              />
              <Input
                aria-label="Buscar operação"
                placeholder="Buscar operação…"
                className="pl-8"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>
          <nav className="min-h-0 flex-1 overflow-y-auto p-2">
            {grouped.map(([tag, items]) => (
              <div key={tag} className="mb-2">
                <div className="px-2 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-subtle uppercase">
                  {tag}
                </div>
                {items.map((operation) => (
                  <button
                    key={operation.operationId}
                    type="button"
                    disabled={!operation.allowed}
                    title={operation.allowed ? operation.summary : 'Seu perfil não tem permissão.'}
                    onClick={() => setSelectedId(operation.operationId)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50',
                      selectedId === operation.operationId &&
                        'bg-primary-soft text-primary hover:bg-primary-soft',
                    )}
                  >
                    <span
                      className={cn(
                        'w-12 shrink-0 font-mono text-[11px] font-semibold',
                        METHOD_TONE[operation.method],
                      )}
                    >
                      {operation.method}
                    </span>
                    <span className="truncate">{operation.summary}</span>
                    {isDestructive(operation) && (
                      <AlertTriangle
                        className="ml-auto size-3.5 shrink-0 text-warning"
                        aria-label="Ação destrutiva"
                      />
                    )}
                  </button>
                ))}
              </div>
            ))}
            {grouped.length === 0 && <Empty title="Nada encontrado" />}
          </nav>
        </CardShell>

        <CardShell as="section" aria-label="Requisição" className="flex flex-col">
          {!selected ? (
            <Empty title="Escolha uma operação" icon={<Terminal />}>
              <p className="muted m-0">O catálogo à esquerda é o contrato público, inteiro.</p>
            </Empty>
          ) : (
            <>
              <div className="border-b border-border px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn('font-mono text-xs font-bold', METHOD_TONE[selected.method])}>
                    {selected.method}
                  </span>
                  <code className="font-mono text-[13px]">{selected.path}</code>
                  {selected.idempotent && <Badge tone="outline">Idempotency-Key</Badge>}
                </div>
                <p className="mt-1 mb-0 text-sm text-muted-foreground">{selected.summary}</p>
              </div>
              <form
                className="flex-1 p-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  void execute.run();
                }}
              >
                {pathParams.length > 0 && (
                  <fieldset className="m-0 mb-2 border-0 p-0">
                    <legend className="mb-1 text-xs font-semibold text-subtle uppercase">
                      Caminho
                    </legend>
                    {pathParams.map((param) => (
                      <Field
                        key={param.name}
                        label={param.name}
                        htmlFor={`path-${param.name}`}
                        hint={param.description}
                      >
                        <Input
                          id={`path-${param.name}`}
                          className="font-mono"
                          required
                          placeholder={String(param.example ?? '')}
                          value={pathValues[param.name] ?? ''}
                          onChange={(event) =>
                            setPathValues((current) => ({
                              ...current,
                              [param.name]: event.target.value,
                            }))
                          }
                        />
                      </Field>
                    ))}
                  </fieldset>
                )}
                {queryParams.length > 0 && (
                  <fieldset className="m-0 mb-2 border-0 p-0">
                    <legend className="mb-1 text-xs font-semibold text-subtle uppercase">
                      Query
                    </legend>
                    {queryParams.map((param) => (
                      <Field
                        key={param.name}
                        label={param.name}
                        htmlFor={`query-${param.name}`}
                        hint={param.description}
                      >
                        {param.enum ? (
                          <select
                            id={`query-${param.name}`}
                            className={inputClass}
                            value={queryValues[param.name] ?? ''}
                            onChange={(event) =>
                              setQueryValues((current) => ({
                                ...current,
                                [param.name]: event.target.value,
                              }))
                            }
                          >
                            <option value="">—</option>
                            {param.enum.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            id={`query-${param.name}`}
                            className="font-mono"
                            placeholder={String(param.example ?? '')}
                            value={queryValues[param.name] ?? ''}
                            onChange={(event) =>
                              setQueryValues((current) => ({
                                ...current,
                                [param.name]: event.target.value,
                              }))
                            }
                          />
                        )}
                      </Field>
                    ))}
                  </fieldset>
                )}
                {selected.body.length > 0 && (
                  <Field
                    label="Corpo (JSON)"
                    htmlFor="body"
                    hint={
                      bodyError ??
                      `Campos: ${selected.body.map((f) => f.name + (f.required ? '*' : '')).join(', ')}`
                    }
                  >
                    <Textarea
                      id="body"
                      className="min-h-40 font-mono text-[12.5px]"
                      value={bodyText}
                      onChange={(event) => setBodyText(event.target.value)}
                      aria-invalid={bodyError ? true : undefined}
                      spellCheck={false}
                    />
                  </Field>
                )}
                <div className="flex items-center gap-2">
                  <Button type="submit" disabled={execute.pending || missingPath}>
                    <Play /> {execute.pending ? 'Executando…' : 'Executar'}
                  </Button>
                  <span className="text-xs text-subtle">
                    Executa com a sua sessão, na sua organização.
                  </span>
                </div>
                {execute.error != null && (
                  <div className="mt-3">
                    <ErrorState error={execute.error} />
                  </div>
                )}
              </form>
              {result && (
                <section aria-label="Resposta" className="border-t border-border p-4">
                  <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                    <Badge tone={result.ok ? 'ok' : result.status >= 500 ? 'danger' : 'warn'}>
                      HTTP {result.status}
                    </Badge>
                    <span className="text-muted-foreground">{result.durationMs} ms</span>
                    {result.requestId && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <code className="font-mono text-xs">{result.requestId}</code>
                        <CopyButton
                          value={result.requestId}
                          label="Copiar request id"
                          variant="ghost"
                          size="icon-xs"
                        />
                      </span>
                    )}
                  </div>
                  <pre className="code max-h-80 text-[12px]">{formatBody(result.body)}</pre>
                </section>
              )}
            </>
          )}
        </CardShell>

        <CardShell as="section" aria-label="Código" className="flex flex-col">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <h3 className="m-0 text-sm font-semibold">Código</h3>
            <Tabs value={language} onValueChange={setLanguage}>
              <TabsList>
                {(samples.data?.languages ?? Object.keys(LANGUAGE_LABEL)).map((key) => (
                  <TabsTrigger key={key} value={key}>
                    {LANGUAGE_LABEL[key] ?? key}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="relative flex-1 p-4">
            {!selected ? (
              <Empty title="O código aparece aqui" />
            ) : samples.loading && !samples.data ? (
              <Loading />
            ) : (
              <>
                <div className="absolute top-6 right-6">
                  <CopyButton value={samples.data?.samples[language] ?? ''} label="Copiar código" />
                </div>
                <pre className="code min-h-40 text-[12px]" data-testid="code-sample">
                  {samples.data?.samples[language] ?? ''}
                </pre>
                <p className="mt-2 mb-0 text-xs text-subtle">
                  Troque <code>tk_live_SUA_CHAVE_AQUI</code> por uma API key. A sessão do console
                  nunca entra no código.
                </p>
              </>
            )}
          </div>
        </CardShell>
      </div>
    </>
  );
}

/**
 * Rotas que apagam, desconectam ou revogam algo. O alerta na lista existe para o dedo
 * parar antes do clique: no Explorer a chamada é real, contra a instância real.
 */
export function isDestructive(
  operation: Pick<ExplorerOperation, 'method' | 'operationId'>,
): boolean {
  return (
    operation.method === 'DELETE' ||
    /logout|disconnect|revoke|delete|remove|restart/i.test(operation.operationId)
  );
}

function groupByTag(operations: ExplorerOperation[]): Array<[string, ExplorerOperation[]]> {
  const groups = new Map<string, ExplorerOperation[]>();
  for (const operation of operations) {
    groups.set(operation.tag, [...(groups.get(operation.tag) ?? []), operation]);
  }
  return [...groups.entries()];
}

function defaultFor(type: string): unknown {
  switch (type) {
    case 'integer':
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'object':
      return {};
    default:
      return '';
  }
}

function formatBody(body: unknown): string {
  if (typeof body === 'string') return body;
  return JSON.stringify(body, null, 2);
}
